import { useState } from "react";

import {
  DEFAULT_BUDGET_BOUNDS,
  DEFAULT_FEED_DAILY_BUDGET,
  DEFAULT_PER_HOUR_BOUNDS,
  FEED_BUDGET_MODES,
  FEED_DAILY_BUDGET_RANGE,
  GROUP_BUDGET_MODES,
  PER_HOUR_LIMITS,
} from "../api/mappers.js";

/**
 * CreateProjectForm
 * Props:
 *   initialValues — optional, for editing an existing project
 *   onSubmit({ name, callbackUrl, mode, feedBudgetMode, feedDailyBudget,
 *              feedMinPerHour, feedMaxPerHour, groupBudgetMode, minBudget, maxBudget })
 */
/**
 * hoursBetween renders a daily allowance as the gap between polls, because "12
 * polls a day" is not something anybody can picture and "every two hours" is.
 */
function hoursBetween(perDay) {
  if (perDay <= 0) return "never";
  const minutes = Math.round((24 * 60) / perDay);
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} h`;
}

const sectionLabel = {
  fontSize: "var(--text-sm)",
  color: "var(--text-secondary)",
  display: "block",
  marginBottom: 6,
};
const boundLabel = {
  fontSize: "var(--text-sm)",
  color: "var(--text-secondary)",
  display: "block",
  marginBottom: 4,
};
const boundValue = { color: "var(--text-primary)" };
const rangeCaption = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: "var(--text-xs)",
  color: "var(--text-muted)",
};

export default function CreateProjectForm({ initialValues = {}, onSubmit }) {
  const [name, setName] = useState(initialValues.name || "");
  const [callbackUrl, setCallbackUrl] = useState(initialValues.callbackUrl || "");
  const [mode, setMode] = useState(initialValues.mode || "notify_only"); // 'notify_only' | 'content'
  // How one feed spends its own day.
  const [feedBudgetMode, setFeedBudgetMode] = useState(
    initialValues.feedBudgetMode || FEED_BUDGET_MODES.adaptive,
  );
  const [feedDailyBudget, setFeedDailyBudget] = useState(
    initialValues.feedDailyBudget ?? DEFAULT_FEED_DAILY_BUDGET,
  );
  const [feedMinPerHour, setFeedMinPerHour] = useState(
    initialValues.feedMinPerHour ?? DEFAULT_PER_HOUR_BOUNDS.min,
  );
  const [feedMaxPerHour, setFeedMaxPerHour] = useState(
    initialValues.feedMaxPerHour ?? DEFAULT_PER_HOUR_BOUNDS.max,
  );
  // How the project divides its allowance between its feeds.
  const [groupBudgetMode, setGroupBudgetMode] = useState(
    initialValues.groupBudgetMode || GROUP_BUDGET_MODES.fixed,
  );
  const [minBudget, setMinBudget] = useState(initialValues.minBudget ?? DEFAULT_BUDGET_BOUNDS.min);
  const [maxBudget, setMaxBudget] = useState(initialValues.maxBudget ?? DEFAULT_BUDGET_BOUNDS.max);

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit?.({
      name,
      callbackUrl,
      mode,
      feedBudgetMode,
      // A pinned day only exists in fixed mode; adaptive answers null so no stale
      // number is read as a request for one.
      feedDailyBudget: feedBudgetMode === FEED_BUDGET_MODES.fixed ? feedDailyBudget : null,
      feedMinPerHour,
      feedMaxPerHour,
      groupBudgetMode,
      minBudget,
      maxBudget,
    });
  }

  const toggleBtn = (active) => ({
    flex: 1,
    padding: 8,
    fontSize: "var(--text-sm)",
    border: active ? "2px solid var(--border-accent)" : "1px solid var(--border)",
    background: active ? "var(--bg-accent)" : "var(--surface-0)",
    color: active ? "var(--text-accent)" : "var(--text-primary)",
    borderRadius: "var(--radius)",
    cursor: "pointer",
  });

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 480, margin: "0 auto" }}>
      <p style={{ fontWeight: 500, fontSize: "var(--text-lg)", margin: "0 0 2px" }}>
        {initialValues.name ? "Edit project" : "New project"}
      </p>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 18px" }}>
        A project groups iCal feeds under one webhook destination.
      </p>

      <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Project name</label>
      <input className="ical-input" required placeholder="booking-sync" style={{ width: "100%", marginBottom: 14 }} value={name} onChange={(e) => setName(e.target.value)} />

      <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Callback URL</label>
      <input className="ical-input" type="url" required placeholder="https://api.example.com/hooks/ical" style={{ width: "100%", marginBottom: 14 }} value={callbackUrl} onChange={(e) => setCallbackUrl(e.target.value)} />

      <label style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", display: "block", marginBottom: 6 }}>Delivery mode</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <button type="button" style={toggleBtn(mode === "notify_only")} onClick={() => setMode("notify_only")}>Notify only</button>
        <button type="button" style={toggleBtn(mode === "content")} onClick={() => setMode("content")}>Send content</button>
      </div>

      <label style={sectionLabel}>Feed budget</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button type="button" style={toggleBtn(feedBudgetMode === FEED_BUDGET_MODES.fixed)} onClick={() => setFeedBudgetMode(FEED_BUDGET_MODES.fixed)}>Fixed</button>
        <button type="button" style={toggleBtn(feedBudgetMode === FEED_BUDGET_MODES.adaptive)} onClick={() => setFeedBudgetMode(FEED_BUDGET_MODES.adaptive)}>Adaptive</button>
      </div>

      {feedBudgetMode === FEED_BUDGET_MODES.fixed ? (
        <div style={{ marginBottom: 18 }}>
          <label style={boundLabel}>
            <strong style={boundValue}>{feedDailyBudget}</strong> polls a day works out at one every{" "}
            <strong style={boundValue}>{hoursBetween(feedDailyBudget)}</strong>
          </label>
          <input
            type="range"
            min={FEED_DAILY_BUDGET_RANGE.min}
            max={FEED_DAILY_BUDGET_RANGE.max}
            value={feedDailyBudget}
            aria-label="Polls per feed per day"
            onChange={(e) => setFeedDailyBudget(Number(e.target.value))}
            style={{ width: "100%" }}
          />
          <div style={rangeCaption}>
            <span>{FEED_DAILY_BUDGET_RANGE.min} a day</span>
            <span>{FEED_DAILY_BUDGET_RANGE.max} a day</span>
          </div>
        </div>
      ) : (
        <div className="ical-card" style={{ marginBottom: 18 }}>
          <p style={{ fontSize: "var(--text-sm)", margin: "0 0 6px" }}>
            The feed&apos;s own change history decides when its polls land
          </p>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "0 0 14px" }}>
            Nothing is pinned, so the day is shaped by when this calendar usually moves. The two
            bounds stop that turning into one hot window: the quietest hour still gets its floor,
            and the busiest is capped. Lower the ceiling to flatten a bursty feed, or raise it to
            follow one that changes in sharp peaks. Billing stays the same either way.
          </p>

          {/*
            Two sliders, and raising one carries the other rather than refusing
            the input: a customer dragging the floor up past the ceiling means
            "and the ceiling too", not "that was invalid".
          */}
          <label style={boundLabel}>
            Quietest hour — at least <strong style={boundValue}>{feedMinPerHour}</strong> polls
          </label>
          <input
            type="range"
            min={PER_HOUR_LIMITS.min}
            max={feedMaxPerHour}
            value={feedMinPerHour}
            aria-label="Minimum polls per hour"
            onChange={(e) => {
              const next = Number(e.target.value);
              setFeedMinPerHour(next);
              if (next > feedMaxPerHour) setFeedMaxPerHour(next);
            }}
            style={{ width: "100%" }}
          />

          <label style={{ ...boundLabel, marginTop: 10 }}>
            Busiest hour — at most <strong style={boundValue}>{feedMaxPerHour}</strong> polls
          </label>
          <input
            type="range"
            min={feedMinPerHour}
            max={PER_HOUR_LIMITS.max}
            value={feedMaxPerHour}
            aria-label="Maximum polls per hour"
            onChange={(e) => {
              const next = Number(e.target.value);
              setFeedMaxPerHour(next);
              if (next < feedMinPerHour) setFeedMinPerHour(next);
            }}
            style={{ width: "100%" }}
          />

          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "12px 0 0" }}>
            That is {feedMinPerHour} to {feedMaxPerHour} polls an hour around the clock — up to{" "}
            {feedMaxPerHour * 24} on a busy day, and never fewer than {feedMinPerHour * 24}.
          </p>
        </div>
      )}

      <label style={sectionLabel}>Group budget</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button type="button" style={toggleBtn(groupBudgetMode === GROUP_BUDGET_MODES.fixed)} onClick={() => setGroupBudgetMode(GROUP_BUDGET_MODES.fixed)}>Fixed</button>
        <button type="button" style={toggleBtn(groupBudgetMode === GROUP_BUDGET_MODES.adaptive)} onClick={() => setGroupBudgetMode(GROUP_BUDGET_MODES.adaptive)}>Adaptive</button>
      </div>

      {groupBudgetMode === GROUP_BUDGET_MODES.adaptive ? (
        <div className="ical-card" style={{ marginBottom: 18 }}>
          <p style={{ fontSize: "var(--text-sm)", margin: "0 0 6px" }}>
            Moves the project&apos;s polls to the feeds that actually change
          </p>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "0 0 14px" }}>
            The project keeps the same total — 96 polls per feed. Busier feeds are given more of it,
            quieter ones less. Billing stays the same either way.
          </p>

          {/*
            Two sliders, and raising one carries the other rather than refusing
            the input: a customer dragging the floor up past the ceiling means
            "and the ceiling too", not "that was invalid".
          */}
          <label style={boundLabel}>
            Quietest feed — at least <strong style={boundValue}>{minBudget}</strong> polls/day
          </label>
          <input
            type="range"
            min={1}
            max={300}
            value={minBudget}
            aria-label="Minimum polls per feed per day"
            onChange={(e) => {
              const next = Number(e.target.value);
              setMinBudget(next);
              if (next > maxBudget) setMaxBudget(next);
            }}
            style={{ width: "100%" }}
          />

          <label style={{ ...boundLabel, marginTop: 10 }}>
            Busiest feed — at most <strong style={boundValue}>{maxBudget}</strong> polls/day
          </label>
          <input
            type="range"
            min={1}
            max={300}
            value={maxBudget}
            aria-label="Maximum polls per feed per day"
            onChange={(e) => {
              const next = Number(e.target.value);
              setMaxBudget(next);
              if (next < minBudget) setMinBudget(next);
            }}
            style={{ width: "100%" }}
          />

          {/*
            Two things a reader gets wrong from the sliders alone, said plainly:
            the intervals describe an evenly-spread feed rather than a guarantee
            for every hour, and a new feed is not yet part of the ranking.
          */}
          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "12px 0 0" }}>
            {minBudget} polls a day works out at one every {hoursBetween(minBudget)}, and {maxBudget}{" "}
            at one every {hoursBetween(maxBudget)}. Those are averages for a calendar that changes
            evenly through the day.
          </p>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "6px 0 0" }}>
            Most calendars are not like that. A feed that changes in the afternoon gets fewer polls
            at night and more when it actually moves — each feed still runs its own adaptive
            schedule, it just gets a bigger or smaller share of the group&apos;s polls to spend.
          </p>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", margin: "6px 0 0" }}>
            A feed added from now on sits at 96 a day until it has a day of history to be ranked by.
          </p>
        </div>
      ) : (
        <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "0 0 18px" }}>
          Every feed in this project gets the same daily budget, whatever its own feed budget says.
        </p>
      )}

      <button type="submit" className="ical-btn ical-btn-primary" style={{ width: "100%" }}>
        {initialValues.name ? "Save changes" : "Create project"}
      </button>
    </form>
  );
}
